import React, { useState, useEffect, useContext } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import MainContentLayout from '../../Layout/MainContentLayout'
import { formatCurrency, formatDate } from '../../shared/utils'
import { printPaymentBreakdown } from './printPaymentBreakdown'
import server from '../../../server'
import { AuthenticationContext } from '../../../Auth/authentication.context'
import useTableRowsPerPage from '../../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../../shared/tablePagination'

export default function PaPaymentsBatches() {
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()
  const [batches, setBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [totalItems, setTotalItems] = useState(0)
  const [pagination, setPagination] = useState(() =>
    getInitialPaginationState(user)
  )
  const [pharmacyFilter, setPharmacyFilter] = useState('')
  const [transactionFilter, setTransactionFilter] = useState('')
  const [breakdownVisible, setBreakdownVisible] = useState(false)
  const [breakdown, setBreakdown] = useState(null)
  const [breakdownLoading, setBreakdownLoading] = useState(false)
  const [filterSearchNonce, setFilterSearchNonce] = useState(0)

  useEffect(() => {
    setPagination((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  const fetchBatches = () => {
    setLoading(true)
    const params = new URLSearchParams({
      page: String(pagination.page + 1),
      limit: String(pagination.rows),
      sortBy: 'paidAt',
      sortOrder: 'DESC',
    })
    if (pharmacyFilter.trim()) params.set('pharmacyName', pharmacyFilter.trim())
    if (transactionFilter.trim()) params.set('transactionId', transactionFilter.trim())

    server
      .get(`/admins/payables/batches?${params}`)
      .then((response) => {
        setBatches(response.data.data.batches || [])
        setTotalItems(response.data.data.totalItems || 0)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    fetchBatches()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.rows, filterSearchNonce])

  const handleFilter = () => {
    setPagination((prev) => ({ ...prev, page: 0, first: 0 }))
    setFilterSearchNonce((n) => n + 1)
  }

  const openBreakdown = (batchId) => {
    setBreakdown(null)
    setBreakdownVisible(true)
    setBreakdownLoading(true)
    server
      .get(`/admins/payables/batches/${batchId}`)
      .then((res) => {
        setBreakdown(res.data.data)
        setBreakdownLoading(false)
      })
      .catch(() => setBreakdownLoading(false))
  }

  return (
    <MainContentLayout title='Pay Statements'>
      <div className='d-flex flex-wrap gap-2 align-items-center mb-3'>
        <span className='p-input-icon-left'>
          <i className='pi pi-search' />
          <InputText
            value={pharmacyFilter}
            onChange={(e) => setPharmacyFilter(e.target.value)}
            placeholder='Pharmacy name'
            className='p-inputtext-sm'
            style={{ width: '200px' }}
          />
        </span>
        <span className='p-input-icon-left'>
          <i className='pi pi-id-card' />
          <InputText
            value={transactionFilter}
            onChange={(e) => setTransactionFilter(e.target.value)}
            placeholder='Transaction / Cheque ID'
            className='p-inputtext-sm'
            style={{ width: '220px' }}
          />
        </span>
        <Button label='Search' icon='pi pi-search' onClick={handleFilter} className='p-button-sm' />
      </div>

      <DataTable
        value={batches}
        loading={loading}
        dataKey='id'
        showGridlines
        emptyMessage='No payments recorded yet.'
        selectionMode='single'
        onRowSelect={(e) => openBreakdown(e.data.id)}
        rowHover
        paginator
        rows={pagination.rows}
        rowsPerPageOptions={rowsPerPageOptions}
        totalRecords={totalItems}
        first={pagination.first}
        onPage={(e) =>
          setPagination((prev) => applyPageEvent(prev, e))
        }
        paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
        currentPageReportTemplate='Showing {first} to {last} of {totalRecords} payments'
      >
        <Column field='id' header='Payment ID' body={(row) => row.id} />
        <Column
          field='paidAt'
          header='Date'
          body={(row) => (row.paidAt ? formatDate(row.paidAt) : '-')}
          sortable
        />
        <Column field='pharmacyName' header='Pharmacy' body={(row) => row.pharmacyName || '-'} />
        <Column field='transactionId' header='Transaction / Cheque ID' body={(row) => row.transactionId || '-'} />
        <Column
          field='totalPaAmount'
          header='Amount'
          body={(row) => formatCurrency(row.totalPaAmount)}
        />
        <Column field='ordersCount' header='Orders' />
      </DataTable>

      <Dialog
        header={breakdown ? `Payment #${breakdown.paymentId} – Breakdown` : 'Payment breakdown'}
        visible={breakdownVisible}
        onHide={() => setBreakdownVisible(false)}
        style={{ width: 'min(90vw, 560px)' }}
        modal
        footer={
          breakdown && !breakdownLoading ? (
            <Button
              label='Print'
              icon='pi pi-print'
              onClick={() => printPaymentBreakdown(breakdown, { formatDate, formatCurrency, showPharmacy: true })}
            />
          ) : null
        }
      >
        {breakdownLoading && <p>Loading...</p>}
        {!breakdownLoading && breakdown && (
          <div>
            <div className='d-flex flex-wrap gap-3 mb-3'>
              <span><strong>Payment ID:</strong> {breakdown.paymentId}</span>
              <span><strong>Date:</strong> {formatDate(breakdown.paidAt)}</span>
              <span><strong>Pharmacy:</strong> {breakdown.pharmacyName}</span>
              <span><strong>Transaction / Cheque:</strong> {breakdown.transactionId}</span>
              <span><strong>Total amount:</strong> {formatCurrency(breakdown.totalPaAmount)}</span>
            </div>
            <h6 className='mb-2'>Orders included</h6>
            <DataTable
              value={breakdown.orders || []}
              dataKey='id'
              size='small'
              showGridlines
            >
              <Column field='supplierInvoiceNo' header='Order No.' />
              <Column field='createdAt' header='Date' body={(r) => (r.createdAt ? formatDate(r.createdAt) : '-')} />
              <Column field='confirmedExtendTotal' header='Confirmed value' body={(r) => formatCurrency(r.confirmedExtendTotal)} />
              <Column field='confirmedPaValue' header='PA amount' body={(r) => formatCurrency(r.confirmedPaValue)} />
            </DataTable>
          </div>
        )}
      </Dialog>
    </MainContentLayout>
  )
}
